import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import { Body2 } from 'components/atoms/Text';
import DropDown from 'components/organisms/DropDown/DropDown';
import DropDownInput from 'components/organisms/DropDown/DropDownInput';
import { useViewport } from 'contexts/viewport';
import useClickOutside from 'services/hooks/useClickOutside';

const DropDownContainer = styled.div`
    width: 100%;
    min-width: 250px;
    height: fit-content;
    position: relative;
    padding: 12px 0;
`;

const DropDownListContainer = styled.div`
    position: relative;
`;

const StyledBody2 = styled(Body2)`
    display: block;
    margin-bottom: 8px;
`;

const DropDownWrapper = ({
    id,
    className,
    disabled = false,
    required = false,
    title = '',
    renderOption,
    shouldFilter = false,
    shouldIgnoreOverflow = false,
    typable = true,
    itemList,
    value = {},
    onChange,
    onOpenChange = () => {},
}) => {
    const inputRef = useRef(null);
    const dropDownRef = useRef(null);
    const { height } = useViewport();

    const [direction, setDirection] = useState('down');
    const [dropDownValue, setDropDownValue] = useState(value.title || '');
    const [dropDownOpen, setDropDownOpen] = useState(false);

    const handleCallback = (data) => {
        if (typable) {
            setDropDownValue(data);
        }
    };

    const handleSelected = (e, data) => {
        onChange(data);
        setDropDownValue(data.title);
        onOpenChange(false);
        setDropDownOpen(false);
    };

    const handleClick = () => {
        if (disabled) return;

        if (!dropDownOpen) {
            const { bottom } = inputRef.current?.getBoundingClientRect();
            if (bottom + 207 > height) {
                setDirection('up');
            } else {
                setDirection('down');
            }
        }

        onOpenChange(!dropDownOpen);
        setDropDownOpen((prev) => !prev);
    };

    if (shouldFilter && dropDownValue) {
        itemList = itemList.filter(({ title }) => title.toLowerCase().includes(dropDownValue.toLowerCase()));
    }

    useClickOutside(dropDownRef, () => {
        onOpenChange(false);
        setDropDownOpen(false);
    });

    useEffect(() => {
        if (dropDownOpen && (!typable || (typable && document.activeElement !== inputRef.current)))
            dropDownRef.current.focus();
    }, [dropDownOpen]);

    useEffect(() => {
        setDropDownValue(value.title);
    }, [value.title]);

    return (
        <DropDownContainer id={id} className={className}>
            <StyledBody2>
                {title}
                {required && '*'}
            </StyledBody2>
            <DropDownInput
                id={id}
                ref={inputRef}
                tabIndex={typable ? '1' : '-1'}
                disabled={disabled}
                value={dropDownValue}
                handleChangeText={handleCallback}
                clickListener={handleClick}
                dropDownOpen={dropDownOpen}
                typable={typable}
            />
            <DropDownListContainer>
                <DropDown
                    ref={dropDownRef}
                    open={dropDownOpen}
                    direction={direction}
                    renderOption={renderOption}
                    shouldIgnoreOverflow={shouldIgnoreOverflow}
                    itemList={itemList}
                    handleSelected={handleSelected}
                    handleCancel={() => setDropDownOpen(false)}
                />
            </DropDownListContainer>
        </DropDownContainer>
    );
};

export default DropDownWrapper;
